import { CSSProperties, useContext, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { Slider } from 'radix-ui';
import { Play, Pause, VolumeOff, Volume2, XIcon } from 'lucide-react';

import { AudioPlayerContext } from '../../AudioPlayer.context';

import styles from './BasicPlayer.module.css';

export function BasicPlayer({
	show = true,
	style,
	className,
}: BasicPlayerProps) {
	const { isReady } = useContext(AudioPlayerContext);
	const classNames = [styles['player']];
	if (className) {
		classNames.push(className);
	}
	return (
		<AnimatePresence>
			{show && isReady && (
				<motion.div
					className={classNames.join(' ')}
					style={style}
					initial={{ opacity: 0, scale: 0 }}
					animate={{ opacity: 1, scale: 1 }}
					exit={{ opacity: 0, scale: 0 }}
				>
					<div>
						<PlayPauseBtn />
					</div>
					<ProgressBar />
					<div>
						<MuteBtn />
						<CloseBtn />
					</div>
				</motion.div>
			)}
		</AnimatePresence>
	);
}

interface BasicPlayerProps {
	show?: boolean;
	style?: CSSProperties;
	className?: string;
}

function PlayPauseBtn() {
	const { playPause, isPlaying } = useContext(AudioPlayerContext);
	const color = '#000';
	const size = 20;
	return (
		<button onClick={playPause}>
			<AnimatePresence initial={false}>
				{isPlaying ? (
					<PauseBtn color={color} size={size} />
				) : (
					<PlayBtn color={color} size={size} />
				)}
			</AnimatePresence>
		</button>
	);
}

function MuteBtn() {
	const { muted, toggleMute } = useContext(AudioPlayerContext);
	const color = '#000';
	const size = 20;
	return (
		<button onClick={toggleMute}>
			<AnimatePresence initial={false}>
				{muted ? (
					<VolumeOffBtn color={color} size={size} />
				) : (
					<VolumeBtn color={color} size={size} />
				)}
			</AnimatePresence>
		</button>
	);
}

function CloseBtn() {
	const { close } = useContext(AudioPlayerContext);
	const color = '#ccc';
	const size = 12;
	return (
		<button onClick={close}>
			<XIcon color={color} size={size} />
		</button>
	);
}

interface ButtonProps {
	color: string;
	size: number;
}

function ProgressBar() {
	const [isDragging, setIsDragging] = useState(false);

	const { progress, onProgressChange, setProgress } =
		useContext(AudioPlayerContext);
	const [progressValue, setProgressValue] = useState(progress);
	return (
		<motion.div className={styles['progress-bar']}>
			<MotionRoot
				className={styles['root']}
				value={[isDragging ? progressValue : progress]}
				onValueCommit={(value) => {
					if (!value.length) return;
					const p = value[0];
					setProgress(p);
					setIsDragging(false);
					onProgressChange(p / 100, 'fraction');
					setProgressValue(p);
				}}
				onValueChange={(value) => {
					if (!value.length) return;
					setIsDragging(true);
					const p = value[0];
					setProgressValue(p);
				}}
				defaultValue={[progress]}
				max={100}
				step={1}
			>
				<MotionTrack className={styles['track']}>
					<Slider.Range className={styles['range']} />
				</MotionTrack>
				<MotionThumb
					key={'audio-thumb'}
					exit={{ scale: 0 }}
					className={styles['thumb']}
				/>
			</MotionRoot>
		</motion.div>
	);
}

const MotionRoot = motion.create(Slider.Root);
const MotionTrack = motion.create(Slider.Track);
const MotionThumb = motion.create(Slider.Thumb);

/* BUTTONS */

const MotionPause = motion.create(Pause);
const MotionPlay = motion.create(Play);
const MotionVolume = motion.create(Volume2);
const MotionVolumeOff = motion.create(VolumeOff);

function PauseBtn(props: ButtonProps) {
	const { size } = props;
	const [color, setColor] = useState(props.color);
	return (
		<MotionPause
			initial={{
				opacity: 0,
				scale: 0.5,
			}}
			onHoverStart={() => {
				setColor('var(--app-accent-color)');
			}}
			onHoverEnd={() => {
				setColor(props.color);
			}}
			whileTap={{ scale: 0.9 }}
			animate={{
				scale: 1,
				opacity: 1,
			}}
			exit={{
				opacity: 0,
				scale: 0.5,
			}}
			key={'pause-btn'}
			color={color}
			size={size}
			fill={color}
		/>
	);
}

function PlayBtn(props: ButtonProps) {
	const { size } = props;
	const [color, setColor] = useState(props.color);
	return (
		<MotionPlay
			onHoverStart={() => {
				setColor('var(--app-accent-color)');
			}}
			onHoverEnd={() => {
				setColor(props.color);
			}}
			whileTap={{ scale: 0.9 }}
			initial={{
				opacity: 0,
				scale: 0.5,
			}}
			animate={{
				scale: 1,
				opacity: 1,
			}}
			exit={{
				opacity: 0,
				scale: 0.5,
			}}
			key={'play-btn'}
			color={color}
			size={size}
			fill={color}
		/>
	);
}

function VolumeBtn(props: ButtonProps) {
	const { size } = props;
	const [color, setColor] = useState(props.color);
	return (
		<MotionVolume
			onHoverStart={() => {
				setColor('var(--app-accent-color)');
			}}
			onHoverEnd={() => {
				setColor(props.color);
			}}
			whileTap={{ scale: 0.9 }}
			initial={{
				opacity: 0,
				scale: 0.5,
			}}
			animate={{
				scale: 1,
				opacity: 1,
			}}
			exit={{
				opacity: 0,
				scale: 0.5,
			}}
			key={'volume-btn'}
			color={color}
			size={size}
			fill={color}
		/>
	);
}

function VolumeOffBtn(props: ButtonProps) {
	const { size } = props;
	const [color, setColor] = useState(props.color);
	return (
		<MotionVolumeOff
			onHoverStart={() => {
				setColor('var(--app-accent-color)');
			}}
			onHoverEnd={() => {
				setColor(props.color);
			}}
			whileTap={{ scale: 0.9 }}
			initial={{
				opacity: 0,
				scale: 0.5,
			}}
			animate={{
				scale: 1,
				opacity: 1,
			}}
			exit={{
				opacity: 0,
				scale: 0.5,
			}}
			key={'volume-btn'}
			color={color}
			size={size}
			fill={color}
		/>
	);
}
